import React, { useState, useEffect, useCallback } from "react";
import Styles from "./TermsCondition.module.css";
import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { formatDate, formatSlotTime } from "../../../../helpers/dateFormat";
import TimeRangePicker from "@wojtekmaj/react-timerange-picker";
import "@wojtekmaj/react-timerange-picker/dist/TimeRangePicker.css";
import "react-clock/dist/Clock.css";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import { DateRangePicker } from "react-date-range";
import { addDays, format } from "date-fns";
import * as rdrLocales from "react-date-range/dist/locale";
import ReactDOM from "react-dom";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import DateInput from "../../../../commons/Inputs/DateInputBailateral/DateInput";
import TimeInput from "../../../../commons/Inputs/TimeInputBailateral/TimeInput";
import { toast } from "react-toastify";

const TermsCondition = ({
  open,
  handleClose,
  data,
  handleTermsSubmit,
  selectedBid,
  wholeData,
  loading,
  isEditTerms,
}) => {
  const [state, setState] = useState([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 7),
      key: "selection",
    },
  ]);
  const [uniqueSelfReq, setUniqueSelfReq] = useState([]);
  const [selectedSelfReq, setSelectedSelfReq] = useState(null);
  const [finalValues, setFinalValues] = useState({});
  // Add shared payment values for generators/consumers across all requisitions
  const [sharedPaymentValues, setSharedPaymentValues] = useState({});
  const [conformOpen, setConformOpen] = useState(false);
  const [dataWithIndex, setDataWithIndex] = useState([]);
  const [datePickerOpen, setDatePickerOpen] = useState({}); // Track open picker per counterparty+slot
  const [datePickerTarget, setDatePickerTarget] = useState(null); // Track which input is active

  const mainScreenMatchData = useSelector(
    (state) => state?.CapacitySlice?.mainScreenActiveConnection,
  );

  const isDAMValue = (val) => {
    if (typeof val !== "string") return false;
    return /^DAM\s*[\+\-]\s*\d+$/i.test(val.trim());
  };
  const isNumericValue = (val) => {
    return (
      !isNaN(Number(val)) && val !== "" && val !== null && val !== undefined
    );
  };
  const user = useSelector(
    (state) => state.persistedReducer.user?.activeRole,
  )?.toLowerCase();

  const getFinalizedId = useCallback(() => {
    if (isEditTerms && mainScreenMatchData) {
      let ids = [];
      let accessKey = user === "consumer" ? "generatorSlot" : "consumerSlot";
      for (let i = 0; i < mainScreenMatchData.length; i++) {
        for (
          let j = 0;
          j < mainScreenMatchData[i].bilateralConnections.length;
          j++
        ) {
          ids.push(
            mainScreenMatchData[i].bilateralConnections[j][accessKey].id,
          );
        }
      }

      return ids;
    }
  }, [isEditTerms, mainScreenMatchData, user]);

  const getEditData = useCallback(() => {
    if (!isEditTerms || getFinalizedId().length === 0) return [];

    const accessKey = user === "consumer" ? "generators" : "consumers";
    const finalizedIds = getFinalizedId();
    const copiedWholeData = JSON.parse(JSON.stringify(wholeData));

    const cleanedData = copiedWholeData
      .map((item) => {
        const cleanedMatches = item.matches
          .map((match) => {
            const cleanedEntities = match[accessKey]
              .map((entity) => {
                // Filter matchSlots: include only if id is in finalizedIds AND status is not ACCEPTED
                const cleanedSlots = entity.matchSlots.filter(
                  (slot) =>
                    finalizedIds.includes(slot.id) &&
                    slot.finalizedConnectionValues?.status !== "ACCEPTED",
                );

                return cleanedSlots.length > 0
                  ? { ...entity, matchSlots: cleanedSlots }
                  : null;
              })
              .filter(Boolean);

            return cleanedEntities.length > 0
              ? { ...match, [accessKey]: cleanedEntities }
              : null;
          })
          .filter(Boolean);

        return cleanedMatches.length > 0
          ? { ...item, matches: cleanedMatches }
          : null;
      })
      .filter(Boolean);
    return cleanedData;
  }, [getFinalizedId, isEditTerms, user, wholeData]);

  let finalData = useCallback(() => {
    return isEditTerms ? getEditData() : data;
  }, [data, getEditData, isEditTerms]);

  useEffect(() => {
    // Step 1: Build mapping from selfSlot.id to index in wholeData
    let idToIndexMap = {};
    if (wholeData && Array.isArray(wholeData)) {
      wholeData.forEach((item) => {
        item.matches?.forEach((match, idx) => {
          idToIndexMap[match.selfSlot.id] = idx + 1; // 1-based index
        });
      });
    }

    // Step 2: Apply mapping to both wholeData and data
    const addIndexToMatches = (arr) =>
      arr?.map((item) => ({
        ...item,
        matches:
          item.matches?.map((match) => ({
            ...match,
            selfSlot: {
              ...match.selfSlot,
              index: idToIndexMap[match.selfSlot.id] || 1, // fallback to 1 if not found
            },
          })) ?? [],
      }));

    if (finalData()) setDataWithIndex(addIndexToMatches(finalData()));
  }, [wholeData, finalData]);

  // Determine if user is consumer or generator and get appropriate data
  const isConsumer = user === "consumer";
  const isGenerator = user === "generator";

  // Get counterparties (generators for consumer, consumers for generator)
  const getCounterparties = (match) => {
    return isConsumer ? match.generators : match.consumers;
  };

  // Get capacity data based on user role
  const getCapacityData = () => {
    if (isConsumer) {
      return dataWithIndex[0]?.capacityConsumer || {};
    } else if (isGenerator) {
      return dataWithIndex[0]?.capacityGenerator || {};
    }
    return {};
  };

  // Extract unique requisitions on data change
  useEffect(() => {
    if (!dataWithIndex?.[0]?.matches) return;
    const allSelfSlots = dataWithIndex[0].matches.map((match, i) => ({
      id: i,
      reqId: match.selfSlot.index,
      selfRequisitionIndex: match.selfSlot.id,
    }));
    const unique = allSelfSlots.filter(
      (item, idx, arr) =>
        arr.findIndex(
          (i) => i.selfRequisitionIndex === item.selfRequisitionIndex,
        ) === idx,
    );
    setUniqueSelfReq(unique);
    setSelectedSelfReq(unique[0]);
  }, [dataWithIndex]);

  // Get default values for a counterparty under a requisition
  const getDefaultValues = (
    matchSlotsCount,
    selfSlot,
    matchSlots = [],
    counterparty = null,
  ) => {
    const capacityData = getCapacityData();
    // Check for finalizedPaymentDetails in counterparty
    const finalizedPaymentDetails = counterparty?.finalizedPaymentDetails;

    // Determine if fields should be disabled
    const isDisabled =
      (user === "consumer" && counterparty?.initiatorType === "generator") ||
      (user === "generator" && counterparty?.initiatorType === "consumer");

    let defaults = {
      paymentSecurity:
        finalizedPaymentDetails?.paymentSecurity ??
        selfSlot?.paymentSecurity ??
        capacityData.paymentSecurity ??
        "",
      initialMargin:
        finalizedPaymentDetails?.initialMargin ??
        selfSlot?.initialMargin ??
        capacityData.initialMargin ??
        "",
      emd:
        finalizedPaymentDetails?.emd ?? selfSlot?.emd ?? capacityData.emd ?? "",
    };

    for (let i = 1; i <= matchSlotsCount; i++) {
      const matchSlot = matchSlots[i - 1];
      const finalizedValues = matchSlot?.finalizedConnectionValues;

      // If isEditTerms and finalizedValues exist, use those values
      if (isEditTerms && finalizedValues) {
        defaults[`req${i}_energyRate`] = finalizedValues.amount ?? "";
        defaults[`req${i}_energyRateStr`] =
          finalizedValues.energyRateType ?? "";
        defaults[`req${i}_quantity`] = finalizedValues.quantity ?? "";
        defaults[`req${i}_startDate`] = finalizedValues.dateStartAt ?? "";
        defaults[`req${i}_endDate`] = finalizedValues.dateEndAt ?? "";
        defaults[`req${i}_startTime`] = finalizedValues.slotStartAt ?? "";
        defaults[`req${i}_endTime`] = finalizedValues.slotEndAt ?? "";
      } else if (!isDisabled) {
        // User is initiator - use selfSlot values
        defaults[`req${i}_energyRate`] =
          finalizedValues?.amount ??
          selfSlot?.landingCost ??
          selfSlot?.sellingCost ??
          "";

        defaults[`req${i}_energyRateStr`] = selfSlot?.energyRateType ?? "";

        defaults[`req${i}_quantity`] =
          finalizedValues?.quantity ??
          selfSlot?.sellEnergy ??
          selfSlot?.purchaseEnergy ??
          "";

        defaults[`req${i}_startDate`] =
          finalizedValues?.dateStartAt ?? selfSlot?.dateStartAt ?? "";

        defaults[`req${i}_endDate`] =
          finalizedValues?.dateEndAt ?? selfSlot?.dateEndAt ?? "";

        defaults[`req${i}_startTime`] =
          finalizedValues?.slotStartAt ?? selfSlot?.slotStartAt ?? "";

        defaults[`req${i}_endTime`] =
          finalizedValues?.slotEndAt ?? selfSlot?.slotEndAt ?? "";
      } else {
        // Counterparty is initiator - use finalized or matchSlot values
        defaults[`req${i}_energyRate`] =
          finalizedValues?.amount ??
          matchSlot?.sellingCost ??
          matchSlot?.landingCost ??
          "";

        defaults[`req${i}_energyRateStr`] = selfSlot?.energyRateType ?? "";

        defaults[`req${i}_quantity`] =
          finalizedValues?.quantity ??
          matchSlot?.sellEnergy ??
          matchSlot?.purchaseEnergy ??
          "";

        defaults[`req${i}_startDate`] =
          finalizedValues?.dateStartAt ?? matchSlot?.dateStartAt ?? "";

        defaults[`req${i}_endDate`] =
          finalizedValues?.dateEndAt ?? matchSlot?.dateEndAt ?? "";

        defaults[`req${i}_startTime`] =
          finalizedValues?.slotStartAt ?? matchSlot?.slotStartAt ?? "";

        defaults[`req${i}_endTime`] =
          finalizedValues?.slotEndAt ?? matchSlot?.slotEndAt ?? "";
      }
    }
    return defaults;
  };

  // Get current values for a counterparty under a requisition
  const getCurrentValues = (counterparty, matchSlotsCount) => {
    if (!selectedSelfReq || !counterparty) return {};
    const selfSlot =
      dataWithIndex[0]?.matches?.[selectedSelfReq.id]?.selfSlot || {};
    const matchSlots = counterparty.matchSlots || [];
    const defaultValues = getDefaultValues(
      matchSlotsCount,
      selfSlot,
      matchSlots,
      counterparty,
    );
    const storedValues =
      finalValues?.[selectedSelfReq.selfRequisitionIndex]?.[
        counterparty.connectId
      ] || {};
    const sharedValues = sharedPaymentValues[counterparty.connectId] || {};

    return {
      ...defaultValues,
      ...storedValues,
      // Override with shared payment values
      paymentSecurity:
        sharedValues.paymentSecurity ?? defaultValues.paymentSecurity,
      initialMargin: sharedValues.initialMargin ?? defaultValues.initialMargin,
      emd: sharedValues.emd ?? defaultValues.emd,
    };
  };

  // Add this helper at the top (if not already present)
  const isValidEnergyRate = (value) => {
    return true;
    // if (value === undefined || value === null || value === "") return false;
    // if (!isNaN(Number(value))) return true;
    // return /^(DAM\s*[+-]\s*\d+|\d+)$/.test(value.trim());
  };

  // Handle input change for a field
  const handleInputChange = (e, key, counterparty, matchSlotsCount) => {
    if (!selectedSelfReq || !counterparty) return;

    // Support both event (with .target.value) and direct value (Date object/string)
    let value;
    if (e && typeof e === "object" && "target" in e && "value" in e.target) {
      value = e.target.value;
    } else {
      // e is likely a Date object or string
      value = e instanceof Date ? format(e, "yyyy-MM-dd") : e;
    }

    // Parse energy rate input to extract type and amount
    if (key.includes("energyRate") && !key.includes("energyRateStr")) {
      let energyRateType = "FIXED";
      let amount = value;

      // Check if value is DAM format
      if (typeof value === "string") {
        const trimmedValue = value.trim().toUpperCase();

        if (trimmedValue.startsWith("DAM+")) {
          energyRateType = "DAM_PLUS";
          amount = trimmedValue.replace("DAM+", "").trim();
        } else if (trimmedValue.startsWith("DAM-")) {
          energyRateType = "DAM_MINUS";
          amount = trimmedValue.replace("DAM-", "").trim();
        } else {
          // It's a fixed rate
          energyRateType = "FIXED";
          amount = value;
        }
      }

      // Update both energyRate and energyRateStr
      const reqIndex = key.match(/req(\d+)_energyRate/)[1];

      setFinalValues((prev) => ({
        ...prev,
        [selectedSelfReq.selfRequisitionIndex]: {
          ...(prev[selectedSelfReq.selfRequisitionIndex] || {}),
          [counterparty.connectId]: {
            ...getCurrentValues(counterparty, matchSlotsCount),
            [`req${reqIndex}_energyRate`]: amount,
            [`req${reqIndex}_energyRateStr`]: energyRateType,
          },
        },
      }));

      return;
    }

    const isPaymentField = ["paymentSecurity", "initialMargin", "emd"].includes(
      key,
    );

    if (isPaymentField) {
      setSharedPaymentValues((prev) => ({
        ...prev,
        [counterparty.connectId]: {
          ...prev[counterparty.connectId],
          [key]: value,
        },
      }));

      setFinalValues((prev) => {
        const newFinalValues = { ...prev };
        dataWithIndex.forEach((item) => {
          if (!item.matches) return;
          item.matches.forEach((match) => {
            const selfReqIndex = match.selfSlot.id;
            const counterparties = getCounterparties(match);
            const hasThisCounterparty = counterparties.some(
              (cp) => cp.connectId === counterparty.connectId,
            );
            if (hasThisCounterparty) {
              if (!newFinalValues[selfReqIndex]) {
                newFinalValues[selfReqIndex] = {};
              }
              if (!newFinalValues[selfReqIndex][counterparty.connectId]) {
                newFinalValues[selfReqIndex][counterparty.connectId] = {};
              }
              newFinalValues[selfReqIndex][counterparty.connectId] = {
                ...newFinalValues[selfReqIndex][counterparty.connectId],
                [key]: value,
              };
            }
          });
        });
        return newFinalValues;
      });
    } else {
      setFinalValues((prev) => ({
        ...prev,
        [selectedSelfReq.selfRequisitionIndex]: {
          ...(prev[selectedSelfReq.selfRequisitionIndex] || {}),
          [counterparty.connectId]: {
            ...getCurrentValues(counterparty, matchSlotsCount),
            [key]: value,
          },
        },
      }));
    }
  };
  // Navigation handlers
  const goToPrevReq = () => {
    const currentIdx = uniqueSelfReq.findIndex(
      (req) =>
        req.selfRequisitionIndex === selectedSelfReq?.selfRequisitionIndex,
    );
    if (currentIdx > 0) setSelectedSelfReq(uniqueSelfReq[currentIdx - 1]);
  };

  const goToNextReq = () => {
    const currentIdx = uniqueSelfReq.findIndex(
      (req) =>
        req.selfRequisitionIndex === selectedSelfReq?.selfRequisitionIndex,
    );
    if (currentIdx < uniqueSelfReq.length - 1)
      setSelectedSelfReq(uniqueSelfReq[currentIdx + 1]);
  };

  // Save all counterparty fields for current requisition
  const saveCurrentReqFields = () => {
    if (!selectedSelfReq) return;
    const counterparties =
      getCounterparties(dataWithIndex[0]?.matches?.[selectedSelfReq.id]) || [];
    setFinalValues((prev) => ({
      ...prev,
      [selectedSelfReq.selfRequisitionIndex]: {
        ...(prev[selectedSelfReq.selfRequisitionIndex] || {}),
        ...counterparties.reduce((acc, counterparty) => {
          const matchSlotsCount = counterparty.matchSlots?.length || 1;
          acc[counterparty.connectId] = getCurrentValues(
            counterparty,
            matchSlotsCount,
          );
          return acc;
        }, {}),
      },
    }));
  };

  // Helper to get all filled values for submission
  const getAllFilledFinalValues = () => {
    const result = {};
    if (!Array.isArray(dataWithIndex)) return result;

    try {
      // Step 1: Collect all energy rate types for each counterparty across all matches/requisitions
      const counterpartyEnergyTypes = {}; // { [connectId]: array of types }
      const counterpartyNames = {}; // { [connectId]: companyName }

      dataWithIndex.forEach((item) => {
        if (!item.matches) return;

        item.matches.forEach((match) => {
          const selfReqIndex = String(match.selfSlot.id);
          const counterparties = getCounterparties(match);

          counterparties.forEach((counterparty) => {
            const connectId = String(counterparty.connectId);
            const matchSlots = counterparty.matchSlots || [];
            const matchSlotsCount = matchSlots.length;
            const selfSlot = match.selfSlot;

            // Store counterparty name
            counterpartyNames[connectId] = counterparty.companyName;

            const defaultValues = getDefaultValues(
              matchSlotsCount,
              selfSlot,
              matchSlots,
              counterparty,
            );

            const storedValues = finalValues?.[selfReqIndex]?.[connectId] || {};

            // Initialize array for this counterparty if not exists
            if (!counterpartyEnergyTypes[connectId])
              counterpartyEnergyTypes[connectId] = [];

            // Collect energy rate types for this counterparty
            for (let idx = 0; idx < matchSlots.length; idx++) {
              const energyRate =
                storedValues[`req${idx + 1}_energyRate`] ??
                defaultValues[`req${idx + 1}_energyRate`];
              const energyRateType =
                storedValues[`req${idx + 1}_energyRateStr`] ??
                defaultValues[`req${idx + 1}_energyRateStr`];

              // Validate that amount is a valid number
              if (
                energyRate !== "" &&
                energyRate !== undefined &&
                energyRate !== null
              ) {
                if (isNaN(Number(energyRate))) {
                  toast.error(
                    `Invalid energy rate value "${energyRate}" in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}. Please enter a valid number.`,
                  );
                  throw new Error("INVALID_ENERGY_RATE_VALUE");
                }
              }

              // Determine the type based on energyRateType
              if (
                energyRateType === "DAM_PLUS" ||
                energyRateType === "DAM_MINUS"
              ) {
                counterpartyEnergyTypes[connectId].push("DAM");
              } else if (energyRateType === "FIXED") {
                counterpartyEnergyTypes[connectId].push("FIXED");
              } else {
                counterpartyEnergyTypes[connectId].push("OTHER");
              }
            }
          });
        });
      });

      // Step 2: Validate that each counterparty has only one type across all matches/requisitions
      for (const [connectId, typesArr] of Object.entries(counterpartyEnergyTypes)) {
        const uniqueTypes = [...new Set(typesArr)];
        if (uniqueTypes.length > 1) {
          toast.error(
            `All Energy Rate values for counterparty ${counterpartyNames[connectId]} must be of the same type (all FIXED or all DAM-based) across all requisitions.`,
          );
          throw new Error("INCONSISTENT_ENERGY_RATE_TYPE");
        }
      }

      // Step 3: Continue with your existing logic to build the result
      dataWithIndex.forEach((item) => {
        if (!item.matches) return;

        item.matches.forEach((match) => {
          const selfReqIndex = String(match.selfSlot.id);
          result[selfReqIndex] = result[selfReqIndex] || {};

          const counterparties = getCounterparties(match);

          counterparties.forEach((counterparty, i) => {
            const connectId = String(counterparty.connectId);
            const matchSlots = counterparty.matchSlots || [];
            const matchSlotsCount = matchSlots.length;
            const selfSlot = match.selfSlot;

            const defaultValues = getDefaultValues(
              matchSlotsCount,
              selfSlot,
              matchSlots,
              counterparty,
            );

            const storedValues = finalValues?.[selfReqIndex]?.[connectId] || {};
            const sharedValues = sharedPaymentValues[connectId] || {};

            // Check payment fields
            const paymentSecurity =
              sharedValues.paymentSecurity ?? defaultValues.paymentSecurity;
            const initialMargin =
              sharedValues.initialMargin ?? defaultValues.initialMargin;
            const emd = sharedValues.emd ?? defaultValues.emd;

            if (
              paymentSecurity === "" ||
              paymentSecurity === undefined ||
              initialMargin === "" ||
              initialMargin === undefined ||
              emd === "" ||
              emd === undefined
            ) {
              toast.error(
                `All payment fields (Payment Security, Initial Margin, EMD) are required for counterparty ${counterparty.companyName}.`,
              );
              throw new Error("EMPTY_PAYMENT_FIELD");
            }

            for (let idx = 0; idx < matchSlots.length; idx++) {
              const energyRate =
                storedValues[`req${idx + 1}_energyRate`] ??
                defaultValues[`req${idx + 1}_energyRate`];
              const quantity =
                storedValues[`req${idx + 1}_quantity`] ??
                defaultValues[`req${idx + 1}_quantity`];
              const startDate =
                storedValues[`req${idx + 1}_startDate`] ||
                defaultValues[`req${idx + 1}_startDate`];
              const endDate =
                storedValues[`req${idx + 1}_endDate`] ||
                defaultValues[`req${idx + 1}_endDate`];
              const startTime =
                storedValues[`req${idx + 1}_startTime`] ||
                defaultValues[`req${idx + 1}_startTime`];
              const endTime =
                storedValues[`req${idx + 1}_endTime`] ||
                defaultValues[`req${idx + 1}_endTime`];

              // Empty check for slot fields
              if (
                energyRate === "" ||
                energyRate === undefined ||
                quantity === "" ||
                quantity === undefined ||
                startDate === "" ||
                startDate === undefined ||
                endDate === "" ||
                endDate === undefined ||
                startTime === "" ||
                startTime === undefined ||
                endTime === "" ||
                endTime === undefined
              ) {
                toast.error(
                  `All fields are required in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`,
                );
                throw new Error("EMPTY_FIELD");
              }

              // Validate dates only
              if (startDate && endDate) {
                const startDateObj = new Date(startDate);
                const endDateObj = new Date(endDate);

                if (startDateObj > endDateObj) {
                  toast.error(
                    `Start Date cannot be greater than End Date in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`,
                  );
                  throw new Error("INVALID_DATE");
                }
              }

              // Validate times only (when dates are equal)
              if (startTime && endTime) {
                if (startTime > endTime) {
                  toast.error(
                    `Start Time cannot be greater than End Time in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`,
                  );
                  throw new Error("INVALID_TIME");
                }
              }

              // Validate combined date-time
              if (startDate && endDate && startTime && endTime) {
                const startDateTime = new Date(`${startDate}T${startTime}`);
                const endDateTime = new Date(`${endDate}T${endTime}`);

                if (startDateTime >= endDateTime) {
                  toast.error(
                    `Start Date & Time must be before End Date & Time in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`,
                  );
                  throw new Error("INVALID_DATE_TIME");
                }
              }
            }

            result[selfReqIndex][connectId] = {
              ...defaultValues,
              ...storedValues,
              id: matchSlots[i]?.id || "",

              paymentSecurity:
                sharedValues.paymentSecurity ?? defaultValues.paymentSecurity,
              initialMargin:
                sharedValues.initialMargin ?? defaultValues.initialMargin,
              emd: sharedValues.emd ?? defaultValues.emd,
            };
          });
        });
      });
    } catch (err) {
      return null;
    }

    return result;
  };

  // Helper to open picker for a specific counterparty and slot
  const openDatePicker = (connectId, idx) => {
    setDatePickerOpen({ connectId, idx });
    setDatePickerTarget({ connectId, idx });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="child-modal-title"
      aria-describedby="child-modal-description"
    >
      <div className={`popupModal ${Styles.modalMainCont}`}>
        <div className="modalHeading">
          <div style={{ color: "var(--fourth-color-text)", fontWeight: 500 }}>
            {isEditTerms ? "Edit T&C" : "Finalize T&C"}
          </div>
          <Close
            onClick={handleClose}
            style={{ cursor: "pointer" }}
            alterbutton="false"
          />
        </div>
        <div className={Styles.termsMainContainer}>
          {/* Requisition Tabs UI */}
          <div className={Styles.reqTabsContainer}>
            <button
              className={Styles.arrowBtn}
              onClick={goToPrevReq}
              disabled={
                uniqueSelfReq.findIndex(
                  (req) =>
                    req.selfRequisitionIndex ===
                    selectedSelfReq?.selfRequisitionIndex,
                ) === 0
              }
            >
              &laquo;
            </button>
            {uniqueSelfReq?.map((req, idx) => {
              return (
                <button
                  key={req.selfRequisitionIndex}
                  className={
                    selectedSelfReq?.selfRequisitionIndex ===
                    req.selfRequisitionIndex
                      ? Styles.reqTabActive
                      : Styles.reqTab
                  }
                  onClick={() => setSelectedSelfReq(req)}
                >
                  {`Requisition ${req.reqId}`}
                </button>
              );
            })}
            <button
              className={Styles.arrowBtn}
              onClick={goToNextReq}
              disabled={
                uniqueSelfReq.findIndex(
                  (req) =>
                    req.selfRequisitionIndex ===
                    selectedSelfReq?.selfRequisitionIndex,
                ) ===
                uniqueSelfReq.length - 1
              }
            >
              &raquo;
            </button>
          </div>

          <div className={Styles.tableHeadingCustom}>
            <div>Category</div>
            {/* <div>My Listing</div> */}
            <div>Counterparty Listing</div>
            <div className={Styles.finalValues}>Final Values</div>
          </div>
          {selectedSelfReq &&
            dataWithIndex[0]?.matches?.[selectedSelfReq.id] &&
            getCounterparties(dataWithIndex[0].matches[selectedSelfReq.id]).map(
              (item, i) => {
                const matchSlotsCount = item.matchSlots?.length || 1;
                const currentValues = getCurrentValues(item, matchSlotsCount);

                const isDisabled =
                  (user === "consumer" && item.initiatorType === "generator") ||
                  (user === "generator" && item.initiatorType === "consumer");

                const isIntraday = item?.isIntraDay;
                return (
                  <div
                    key={item.connectId}
                    className={Styles.tableDetailsMainContCustom}
                  >
                    <div className={Styles.sectionBox}>
                      <div className={Styles.row}>
                        <div>Name</div>
                        <div style={{ fontWeight: 600, fontSize: "12px" }}>
                          {item.companyName}
                        </div>
                        <div></div>
                      </div>
                      <div className={Styles.row}>
                        <div>Initial Margin</div>
                        {/* <div>{capacityData.initialMargin}%</div> */}
                        <div style={{ fontWeight: 500 }}>
                          {item.initialMargin}%
                        </div>
                        <div>
                          <input
                            style={{
                              borderColor:
                                isDisabled &&
                                item.initialMargin !==
                                  Number(currentValues.initialMargin)
                                  ? "var(--primary-color-text)"
                                  : "",
                            }}
                            disabled={isDisabled || isIntraday}
                            type="number"
                            className={Styles.inputBox}
                            value={currentValues.initialMargin}
                            onChange={(e) =>
                              handleInputChange(
                                e,
                                "initialMargin",
                                item,
                                matchSlotsCount,
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className={Styles.row}>
                        <div>EMD</div>
                        {/* <div>{capacityData.emd}%</div> */}
                        <div style={{ fontWeight: 500 }}>
                          {item.emd} (₹/kWh)
                        </div>
                        <div>
                          <input
                            style={{
                              borderColor:
                                isDisabled &&
                                item.emd !== Number(currentValues.emd)
                                  ? "var(--primary-color-text)"
                                  : "",
                            }}
                            disabled={isDisabled || isIntraday}
                            type="number"
                            className={Styles.inputBox}
                            value={currentValues.emd}
                            onChange={(e) =>
                              handleInputChange(e, "emd", item, matchSlotsCount)
                            }
                          />
                        </div>
                      </div>
                      <div className={Styles.row}>
                        <div>Payment Security</div>
                        {/* <div>{capacityData.paymentSecurity}</div> */}
                        <div style={{ fontWeight: 500 }}>
                          {item.paymentSecurity} Days
                        </div>
                        <div>
                          <input
                            style={{
                              borderColor:
                                isDisabled &&
                                item.paymentSecurity !==
                                  Number(currentValues.paymentSecurity)
                                  ? "var(--primary-color-text)"
                                  : "",
                            }}
                            disabled={isDisabled || isIntraday}
                            type="number"
                            className={Styles.inputBox}
                            value={currentValues.paymentSecurity}
                            onChange={(e) =>
                              handleInputChange(
                                e,
                                "paymentSecurity",
                                item,
                                matchSlotsCount,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                    {item.matchSlots.map((matchSlot, idx) => {
                      return (
                        <div className={Styles.reqSection} key={matchSlot.id}>
                          <div className={Styles.reqHeader}>
                            Requisition {idx + 1}
                          </div>
                          <div className={Styles.row}>
                            <div>Energy Rate</div>
                            <div
                              style={{ fontWeight: 500 }}
                              className={Styles.greenText}
                            >
                              {matchSlot?.energyRateStr ||
                                matchSlot.sellingCost ||
                                matchSlot.landingCost}{" "}
                              (₹/kWh)
                            </div>
                            <div>
                              <input
                                style={{
                                  borderColor:
                                    isDisabled &&
                                    (matchSlot?.energyRateStr ||
                                      matchSlot.sellingCost ||
                                      matchSlot.landingCost) !==
                                      Number(
                                        currentValues[
                                          `req${idx + 1}_energyRate`
                                        ],
                                      )
                                      ? "var(--primary-color-text)"
                                      : "",
                                }}
                                disabled={isDisabled}
                                type="text"
                                className={Styles.inputBox}
                                value={
                                  currentValues[
                                    `req${idx + 1}_energyRateStr`
                                  ] === "FIXED"
                                    ? currentValues[`req${idx + 1}_energyRate`]
                                    : currentValues[
                                          `req${idx + 1}_energyRateStr`
                                        ] === "DAM_PLUS"
                                      ? `DAM+${
                                          currentValues[
                                            `req${idx + 1}_energyRate`
                                          ]
                                        }`
                                      : `DAM-${
                                          currentValues[
                                            `req${idx + 1}_energyRate`
                                          ]
                                        }`
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    e,
                                    `req${idx + 1}_energyRate`,
                                    item,
                                    matchSlotsCount,
                                  )
                                }
                              />
                            </div>
                          </div>
                          <div className={Styles.row}>
                            <div>Quantity</div>
                            <div
                              style={{ fontWeight: 500 }}
                              className={Styles.greenText}
                            >
                              {matchSlot.sellEnergy || matchSlot.purchaseEnergy}{" "}
                              (MW)
                            </div>
                            <div>
                              <input
                                style={{
                                  borderColor:
                                    isDisabled &&
                                    (matchSlot.sellEnergy ||
                                      matchSlot.purchaseEnergy) !==
                                      Number(
                                        currentValues[`req${idx + 1}_quantity`],
                                      )
                                      ? "var(--primary-color-text)"
                                      : "",
                                }}
                                disabled={isDisabled}
                                type="number"
                                className={Styles.inputBox}
                                value={currentValues[`req${idx + 1}_quantity`]}
                                onChange={(e) =>
                                  handleInputChange(
                                    e,
                                    `req${idx + 1}_quantity`,
                                    item,
                                    matchSlotsCount,
                                  )
                                }
                              />
                            </div>
                          </div>
                          <div className={Styles.row}>
                            <div>Start Date</div>
                            <div style={{ fontWeight: 500 }}>
                              {formatDate(matchSlot.dateStartAt)}
                            </div>
                            <div>
                              <DateInput
                                style={{
                                  borderColor:
                                    isDisabled &&
                                    matchSlot.dateStartAt !==
                                      currentValues[`req${idx + 1}_startDate`]
                                      ? "var(--primary-color-text)"
                                      : "",
                                }}
                                value={
                                  currentValues[`req${idx + 1}_startDate`] || ""
                                }
                                disabled={isDisabled || isIntraday}
                                onChange={(e) => {
                                  return handleInputChange(
                                    e,
                                    `req${idx + 1}_startDate`,
                                    item,
                                    matchSlotsCount,
                                  );
                                }}
                                height={32}
                              />
                            </div>
                          </div>
                          <div className={Styles.row}>
                            <div>End Date</div>
                            <div style={{ fontWeight: 500 }}>
                              {formatDate(matchSlot.dateEndAt)}
                            </div>
                            <div>
                              <DateInput
                                value={
                                  currentValues[`req${idx + 1}_endDate`] || ""
                                }
                                style={{
                                  borderColor:
                                    isDisabled &&
                                    matchSlot.dateEndAt !==
                                      currentValues[`req${idx + 1}_endDate`]
                                      ? "var(--primary-color-text)"
                                      : "",
                                }}
                                disabled={isDisabled || isIntraday}
                                onChange={(e) => {
                                  return handleInputChange(
                                    e,
                                    `req${idx + 1}_endDate`,
                                    item,
                                    matchSlotsCount,
                                  );
                                }}
                                height={32}
                              />
                            </div>
                          </div>
                          <div className={Styles.row}>
                            <div>Start Time</div>
                            <div>{formatSlotTime(matchSlot.slotStartAt)}</div>
                            <div>
                              <TimeInput
                                style={{
                                  borderColor:
                                    isDisabled &&
                                    String(
                                      formatSlotTime(matchSlot.slotStartAt),
                                    ) !==
                                      String(
                                        formatSlotTime(
                                          currentValues[
                                            `req${idx + 1}_startTime`
                                          ],
                                        ),
                                      )
                                      ? "var(--primary-color-text)"
                                      : "",
                                }}
                                noTooltip={true}
                                value={
                                  formatSlotTime(
                                    currentValues[`req${idx + 1}_startTime`],
                                  ) || ""
                                }
                                disabled={isDisabled}
                                onChange={(val) =>
                                  handleInputChange(
                                    { target: { value: val } },
                                    `req${idx + 1}_startTime`,
                                    item,
                                    matchSlotsCount,
                                  )
                                }
                                height={32}
                                type="startTime"
                              />
                            </div>
                          </div>
                          <div className={Styles.row}>
                            <div>End Time</div>
                            <div>{formatSlotTime(matchSlot.slotEndAt)}</div>
                            <div>
                              <TimeInput
                                style={{
                                  borderColor:
                                    isDisabled &&
                                    String(
                                      formatSlotTime(matchSlot.slotEndAt),
                                    ) !==
                                      String(
                                        formatSlotTime(
                                          currentValues[
                                            `req${idx + 1}_endTime`
                                          ],
                                        ),
                                      )
                                      ? "var(--primary-color-text)"
                                      : "",
                                }}
                                value={
                                  formatSlotTime(
                                    currentValues[`req${idx + 1}_endTime`],
                                  ) || ""
                                }
                                disabled={isDisabled}
                                onChange={(val) =>
                                  handleInputChange(
                                    { target: { value: val } },
                                    `req${idx + 1}_endTime`,
                                    item,
                                    matchSlotsCount,
                                  )
                                }
                                height={32}
                                type="endTime"
                                startTime={
                                  currentValues[`req${idx + 1}_startTime`] || ""
                                }
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              },
            )}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            padding: "15px 25px",
          }}
        >
          {/* <button
            style={{
              padding: "8px 24px",
              border: "1px solid #FFA55C",
              background: "#fff",
              color: "#FFA55C",
              borderRadius: "6px",
              fontWeight: 500,
              cursor: "pointer",
            }}
            onClick={goToPrevReq}
          >
            Previous
          </button> */}
          <div
            style={{
              display: "flex",

              gap: "16px",
            }}
          >
            {/* <button
              style={{
                padding: "8px 24px",
                border: "none",
                background: "var(--primary-color-text)",
                color: "#fff",
                borderRadius: "6px",
                fontWeight: 500,
                cursor: "pointer",
              }}
              onClick={() => {
                saveCurrentReqFields();
                goToNextReq();
              }}
            >
              Save &amp; Next
            </button> */}
            <button
              style={{
                padding: "12px 24px",
                border: "none",
                background: "var(--disabled-color-background)",
                color: "#808080ff",
                borderRadius: "6px",
                fontWeight: 500,
                cursor: "pointer",
              }}
              onClick={() => {
                handleClose();
              }}
            >
              Cancel
            </button>
            <button
              disabled={loading}
              style={{
                padding: "12px 24px",
                border: "none",
                background: "var(--primary-color-text)",
                color: "#fff",
                borderRadius: "6px",
                fontWeight: 500,
                cursor: loading ? "not-allowed" : "pointer",
              }}
              onClick={() => {
                const filledValues = getAllFilledFinalValues();
                if (!filledValues) return;
                setConformOpen(true);
              }}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>

            <ConfirmationModal
              open={conformOpen}
              loading={loading}
              handleClose={() => setConformOpen(false)}
              handleSubmit={() => {
                const filledValues = getAllFilledFinalValues();

                if (!filledValues) return; // stop if validation failed
                // When editing, pass an edit flag to the submit handler (optional)
                if (isEditTerms) {
                  handleTermsSubmit(filledValues, { isEdit: true });
                } else {
                  handleTermsSubmit(filledValues);
                }
              }}
              header={
                isEditTerms
                  ? "Update Terms Confirmation"
                  : "Finalize Terms Confirmation"
              }
              data={
                isEditTerms
                  ? "You are about to update previously finalized T&C. This will overwrite existing finalized values. Do you want to continue?"
                  : `The T&C will be shared with the ${user === "consumer" ? "seller" : "buyer"} for final approval. All other pending connection requests will be auto-rejected. Do you want to continue?`
              }
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TermsCondition;
